import {
	type AccessorFn,
	type Column,
	type ColumnDef,
	type ColumnDefResolved,
	type CoreOptions,
	type DeepKeys,
	type DeepValue,
	getCoreRowModel,
	getExpandedRowModel,
	getFacetedMinMaxValues,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type Row,
	type RowData,
	type RowModel,
	sortingFns,
	type Table,
	type TableOptions,
	type Updater,
	useVueTable,
} from "@tanstack/vue-table";
import { createContext } from "reka-ui";
import {
	computed,
	type ComputedRef,
	type MaybeRefOrGetter,
	type ShallowReactive,
	shallowReactive,
	shallowRef,
	toValue,
	watch,
} from "vue";
import {
	type DataCollectionState,
	type StorageLocation,
	useDataCollectionState,
} from "./data-collection-state";

export type DataCollectionItem<TData> = {
	row: Row<TData>;
	item: TData;
};

export type DataCollectionContext<TData> = {
	state: DataCollectionState;
	options: ShallowReactive<TableOptions<TData>>;
	manager: Table<TData>;
	items: ComputedRef<DataCollectionItem<TData>[]>;
};

export const [injectDataCollectionContext, provideDataCollectionContext] =
	createContext<DataCollectionContext<any>>("DataCollection");

type ColumnBase<TData extends RowData, TValue = unknown> = Omit<
	ColumnDefResolved<TData, TValue>,
	"cell" | "getUniqueValue" | "footer"
>;

export type DataCollectionOptions = {
	storage?: StorageLocation;
	pagination?: boolean;
	manualPagination?: boolean;
	getRowId?: CoreOptions<any>["getRowId"];
	getSubRows?: CoreOptions<any>["getSubRows"];
};

export interface DataCollectionAccessor<TData extends RowData, TValue = unknown>
	extends ColumnBase<TData, TValue> {
	id?: string;
	field?: keyof TData & string;
	fieldFn?: AccessorFn<TData, TValue>;
}

export function useDataCollection<TData extends RowData>(
	data: MaybeRefOrGetter<TData[]>,
	accessors: MaybeRefOrGetter<DataCollectionAccessor<TData>[]>,
	options?: MaybeRefOrGetter<DataCollectionOptions>
) {
	const [state] = useDataCollectionState(toValue(options || {}).storage || "memory");

	const _data = shallowRef<TData[]>([]);
	watch(
		() => toValue(data),
		(data) => {
			if (data) {
				_data.value = [...data];
			}
		},
		{ immediate: true, deep: true }
	);

	type Model = () => RowModel<TData>;
	type ModelConstructor<T extends any[]> = (...args: T) => Model;

	/**
	 * TanStack Table's internal memoization means that only the *initial* call
	 * to a `getXXXModel` function (like `getRowModel()`) within a reactive context
	 * (e.g., a Vue `computed` property) will properly register dependencies. Subsequent
	 * calls to the same model within that context hit a non-reactive cache.
	 *
	 * This utility force a reactivity registration on the data ref.
	 */
	function makeReactiveModel<Args extends any[], MC extends ModelConstructor<Args>>(
		getter: () => MC
	): (...args: Args) => () => RowModel<TData> {
		const modelCts = getter();

		return (...args) => {
			const model = modelCts(...args);
			return () => {
				void _data.value;
				return model();
			};
		};
	}

	const columns = computed<ColumnDef<TData>[]>(() => {
		const columns: ColumnDefResolved<TData>[] = [];

		for (const [idx, accessor] of toValue(accessors).entries()) {
			const { field, fieldFn, ...rest } = accessor;

			const col: ColumnDefResolved<TData> = {
				accessorKey: field,
				accessorFn: fieldFn,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableGrouping: false,
				enableHiding: false,
				enableMultiSort: false,
				enablePinning: false,
				enableResizing: false,
				enableSorting: false,
				...rest,
			};

			if (col.accessorKey == null && col.id == null) {
				col.id = `column-${idx}`;
			}

			if (col.enableColumnFilter) {
				col.filterFn = (row, id, value) => {
					const rowValue = row.getValue<any>(id);

					// Make both rowValues and value an array of string
					const rowValues = Array.isArray(rowValue) ? rowValue.map(String) : [String(rowValue)];
					const filterValues = Array.isArray(value) ? value.map(String) : [String(value)];

					return rowValues.some((rv) => filterValues.includes(rv));
				};
			}

			if (col.getUniqueValues == null) {
				col.getUniqueValues = (data, idx) => {
					let values: any;
					if (col.accessorKey != null) {
						values = data[col.accessorKey as any];
					} else if (col.accessorFn != null) {
						values = col.accessorFn(data, idx);
					}

					if (Array.isArray(values)) {
						return values;
					} else {
						return [values];
					}
				};
			}

			columns.push(cleanUndefined(col));
		}

		return columns as ColumnDef<TData>[];
	});

	function makeUpdater<T extends keyof DataCollectionState>(name: T) {
		return (updater: Updater<DataCollectionState[T]>) => {
			state[name] = updater instanceof Function ? updater(state[name]) : updater;
		};
	}

	const collator = new Intl.Collator();

	const collectionOptions = shallowReactive<TableOptions<TData>>({
		get data() {
			if (columns.value.length === 0) {
				return [];
			}
			return _data.value;
		},
		get columns() {
			return columns.value;
		},

		state: {
			get columnFilters() {
				return state.fieldFilters;
			},
			get globalFilter() {
				return state.filter;
			},
			get pagination() {
				return state.pagination;
			},
			get rowSelection() {
				return state.selection;
			},
			get sorting() {
				return state.sorting;
			},
		},
		onGlobalFilterChange: makeUpdater("filter"),
		onColumnFiltersChange: makeUpdater("fieldFilters"),
		onPaginationChange: makeUpdater("pagination"),
		onRowSelectionChange: makeUpdater("selection"),
		onSortingChange: makeUpdater("sorting"),

		sortDescFirst: true,
		paginateExpandedRows: false,
		manualPagination: toValue(options)?.manualPagination,

		defaultColumn: {
			sortingFn: (rowA: Row<TData>, rowB: Row<TData>, columnId: string): number => {
				const a = rowA.getValue(columnId);
				const b = rowB.getValue(columnId);

				if (typeof a === "string" && typeof b === "string") {
					return collator.compare(a.trim(), b.trim());
				} else if (Object.prototype.toString.call(a) === "[object Date]") {
					return sortingFns.datetime(rowA, rowB, columnId);
				} else {
					return sortingFns.text(rowA, rowB, columnId);
				}
			},
		},

		getRowId: (originalRow, index, parent) => {
			const opts = toValue(options);
			if (opts?.getRowId) {
				return opts.getRowId(originalRow, index, parent);
			}

			if (originalRow != null && typeof originalRow === "object" && "id" in originalRow) {
				return originalRow.id as string;
			}

			return `${parent ? [parent.id, index].join(".") : index}`;
		},

		autoResetPageIndex: false,

		getSubRows: toValue(options)?.getSubRows,

		getCoreRowModel: makeReactiveModel(getCoreRowModel),
		getSortedRowModel: makeReactiveModel(getSortedRowModel),
		getPaginationRowModel: makeReactiveModel(getPaginationRowModel),
		getFilteredRowModel: makeReactiveModel(getFilteredRowModel),
		getExpandedRowModel: makeReactiveModel(getExpandedRowModel),
		getFacetedRowModel: makeReactiveModel(getFacetedRowModel),
		getFacetedUniqueValues: (table, columnId) => {
			const generator = getFacetedUniqueValues<TData>()(table, columnId);

			return () => {
				const facets = generator();

				return new Map(Array.from(facets.entries()).map(([key, val]) => [key.toString(), val]));
			};
		},
		getFacetedMinMaxValues: getFacetedMinMaxValues(),
	});

	const manager = useVueTable<TData>(collectionOptions);

	// Always reset the pagination on filter or sorting change.
	watch(
		() => [state.filter, state.fieldFilters, state.sorting],
		() => {
			if (columns.value.length === 0) {
				return;
			}
			state.pagination.pageIndex = 0;
		},
		{ flush: "sync" }
	);

	// Only reset pagination on data change, if the current page is empty.
	watch(_data, () => {
		if (columns.value.length === 0) {
			return;
		}

		const pageRows = manager.getPaginationRowModel().rows;
		const totalPages = manager.getPageCount();

		if (pageRows.length === 0 && totalPages > 0 && state.pagination.pageIndex > 0) {
			state.pagination.pageIndex = 0;
		}
	});

	const items = computed(() => {
		if (columns.value.length === 0) {
			return [];
		}
		const { pagination, manualPagination } = toValue(options) || {};

		let rows: Row<TData>[];
		if (pagination && !manualPagination) {
			rows = manager.getRowModel().rows;
		} else {
			rows = manager.getPrePaginationRowModel().rows;
		}

		return rows.map((row) => ({ row, item: row.original }));
	});

	function getField<TKey extends DeepKeys<TData>, TValue extends DeepValue<TData, TKey>>(
		key: TKey
	) {
		const column = manager.getColumn(key);
		if (column == null) {
			throw new Error(`Field ${key} is not defined in the collection`);
		}

		return makeField<TData, TKey, TValue>(column as Column<TData, TValue>);
	}

	const context = {
		manager,
		options: collectionOptions,
		state,
		items,
	} satisfies DataCollectionContext<TData>;

	provideDataCollectionContext(context);

	return { ...context, getField };
}

function makeField<
	TData extends RowData,
	TKey extends DeepKeys<TData>,
	TValue extends DeepValue<TData, TKey> = DeepValue<TData, TKey>,
>(column: Column<TData, TValue>) {
	const fieldFilter = computed(() => (column.getFilterValue() as TValue[]) || []);

	const filterable = computed(() => column.getCanFilter());

	function setFilters(value: TValue[] | undefined) {
		column.setFilterValue(value);
	}

	function toggleFilter(value: TValue) {
		const selectedSet = new Set(fieldFilter.value);
		const isSelected = selectedSet.has(value);

		if (isSelected) {
			selectedSet.delete(value);
		} else {
			selectedSet.add(value);
		}

		const values = Array.from(selectedSet);

		setFilters(values.length !== 0 ? values : undefined);
	}

	const uniqueValues = computed<Map<TValue, number>>(() => {
		return column.getFacetedUniqueValues() || new Map();
	});

	return {
		field: column.id as TKey,
		filterable,
		fieldFilter,
		uniqueValues,
		setFilters,
		toggleFilter,
	};
}

function cleanUndefined<T extends Record<string, any>>(obj: T): T {
	const cleaned = {} as T;
	for (const key in obj) {
		if (obj[key] !== undefined) {
			cleaned[key] = obj[key];
		}
	}

	return cleaned;
}
